import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Check, Trophy, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-partner display config — controls page title, success-screen back-link and
// eyebrow text. Add new partners here as contests scale (e.g. ICA if ever revived).
const PARTNERS = {
  fotbollsmorgon: { name: "Fotbollsmorgon", back_url: "/p/fotbollsmorgon", eyebrow_partner: "Fotbollsmorgon" },
  scandsports:    { name: "Scand Sports",    back_url: "/p/scandsports",    eyebrow_partner: "Scand Sports" },
};

/**
 * ContestPage — shared slogan-submission form. `contestSlug` prop determines
 * which "bucket" the entry lands in (fotbollsmorgon | scandsports). Entries
 * are strictly isolated by contest_slug in the DB, so multiple contests can
 * run in parallel without cross-contamination.
 */
export default function ContestPage({ contestSlug = "fotbollsmorgon" }) {
  const partner = PARTNERS[contestSlug] || PARTNERS.fotbollsmorgon;
  const { lang } = useLang();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slogan, setSlogan] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/contest/${contestSlug}`).then(r => setConfig(r.data))
      .catch(err => devWarn("Contest config failed:", err.message))
      .finally(() => setLoading(false));
  }, [contestSlug]);

  const sloganWords = slogan.trim() ? slogan.trim().split(/\s+/).length : 0;
  const sloganOverLimit = sloganWords > 150;
  const formValid = name.trim() && /^\S+@\S+\.\S+$/.test(email) && slogan.trim() && !sloganOverLimit && terms;

  const t = (key) => {
    const dict = {
      sv: {
        page_title: `Tävla — ${partner.name} × Serava`,
        eyebrow: `Reklamtävling · ${partner.eyebrow_partner} × Serava Sports`,
        hero_title: "Tävla & vinn VIP-upplevelser",
        hero_sub: "Skicka in din bästa slogan och vinn drömpaketet — varje vecka under VM 2026.",
        prize_title: "Veckans prisbord",
        prize_intro: "En vinnare utses varje vecka under VM 2026 och belönas med:",
        prize_1: "2 VIP-platser till Manchester United mot Atletico Madrid i Strawberry Arena · 1 augusti 2026",
        prize_2: "500 kr presentkort hos Serava Sports",
        prize_3: "2 biljetter till en Allsvensk match",
        fb_title: "",
        fb_help: "",
        fb_fotbollsmorgon: "",
        fb_serava: "",
        fb_confirm: "",
        slogan_title: "Steg 1 — Din slogan",
        slogan_help: "Max 150 ord. En jury utsedd av anordnaren bedömer alla inskickade sloganer.",
        slogan_words: "ord",
        slogan_overlimit: "för många ord — max 150",
        contact_title: "Steg 2 — Dina uppgifter",
        name_label: "Namn",
        email_label: "E-post",
        phone_label: "Telefon (frivilligt)",
        terms_accept: "Jag har läst och godkänner",
        terms_link: "tävlingsvillkoren",
        submit: "Skicka mitt bidrag",
        submitting: "Skickar...",
        contest_validation_error: "Fyll i alla obligatoriska fält och godkänn villkoren",
        success_title: "Tack — ditt bidrag är inne",
        success_sub: "Vi har tagit emot din anmälan. Veckans vinnare kontaktas via den e-post du angav.",
        success_back: `Tillbaka till ${partner.name}`,
      },
      en: {
        page_title: `Contest — ${partner.name} × Serava`,
        eyebrow: `Promotional contest · ${partner.eyebrow_partner} × Serava Sports`,
        hero_title: "Enter & win VIP experiences",
        hero_sub: "Submit your best slogan and win the dream package — every week during the 2026 World Cup.",
        prize_title: "This week's prize",
        prize_intro: "One winner is selected each week during the 2026 World Cup and receives:",
        prize_1: "2 VIP seats to Manchester United vs Atletico Madrid at Strawberry Arena · August 1, 2026",
        prize_2: "500 SEK gift card with Serava Sports",
        prize_3: "2 tickets to an Allsvenskan match",
        fb_title: "",
        fb_help: "",
        fb_fotbollsmorgon: "",
        fb_serava: "",
        fb_confirm: "",
        slogan_title: "Step 1 — Your slogan",
        slogan_help: "Max 150 words. A jury appointed by the organiser reviews all submitted slogans.",
        slogan_words: "words",
        slogan_overlimit: "too many words — max 150",
        contact_title: "Step 2 — Your details",
        name_label: "Name",
        email_label: "Email",
        phone_label: "Phone (optional)",
        terms_accept: "I have read and accept the",
        terms_link: "contest terms",
        submit: "Submit my entry",
        submitting: "Submitting...",
        contest_validation_error: "Fill in all required fields and accept the terms",
        success_title: "Thank you — your entry is in",
        success_sub: "We've received your submission. The weekly winner is contacted via the email you provided.",
        success_back: `Back to ${partner.name}`,
      },
    };
    return dict[lang]?.[key] || dict.sv[key];
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formValid) { toast.error(t("contest_validation_error")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contest/submit`, {
        contest_slug: contestSlug, name, email, phone, slogan,
        facebook_followed: true, terms_accepted: terms, lang,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "en" ? "Submission failed" : "Inskickning misslyckades"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A]" />;
  if (!config) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <p className="font-['Outfit']">{lang === "en" ? "Contest not available right now." : "Tävlingen är inte tillgänglig just nu."}</p>
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Helmet><title>{t("page_title")}</title></Helmet>
        <Navbar />
        <main className="max-w-2xl mx-auto px-6 py-24" data-testid="contest-success">
          <div className="border border-[#C5A059]/40 bg-gradient-to-b from-[#C5A059]/5 to-transparent p-10 text-center">
            <div className="w-14 h-14 mx-auto border border-[#C5A059] flex items-center justify-center mb-6">
              <Check size={24} className="text-[#C5A059]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl text-white font-light mb-3">{t("success_title")}</h1>
            <p className="text-white/70 font-['Outfit'] mb-8">{t("success_sub")}</p>
            <Link to={partner.back_url} className="inline-flex items-center gap-2 text-white/80 hover:text-[#C5A059] font-['Outfit'] text-xs tracking-[0.18em] uppercase border border-white/20 hover:border-[#C5A059] px-6 py-3 transition-colors" data-testid="contest-success-back">
              {t("success_back")} <ArrowRight size={14} />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const labelCls = "block text-white/55 text-[10px] font-['Outfit'] tracking-[0.18em] uppercase mb-2";
  const inputCls = "w-full bg-[#0E0E0E] border border-[#262626] focus:border-[#C5A059] outline-none text-white px-4 py-3 font-['Outfit'] text-sm transition-colors placeholder:text-white/25";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Helmet>
        <title>{t("page_title")}</title>
        <meta name="description" content={t("hero_sub")} />
      </Helmet>
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <p className="text-[#C5A059] text-[11px] tracking-[0.22em] uppercase font-['Outfit'] mb-4">{t("eyebrow")}</p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white font-light leading-[1.05] mb-5">{t("hero_title")}</h1>
          <p className="text-white/70 font-['Outfit'] max-w-xl mx-auto leading-relaxed">{t("hero_sub")}</p>
        </div>

        {/* Prize */}
        <div className="border border-[#C5A059]/30 bg-gradient-to-b from-[#C5A059]/[0.08] to-transparent p-8 mb-12 text-center">
          <Trophy size={28} className="text-[#C5A059] mx-auto mb-4" />
          <h2 className="font-['Cormorant_Garamond'] text-3xl text-white font-light mb-3">{t("prize_title")}</h2>
          <p className="text-white/65 font-['Outfit'] text-sm max-w-lg mx-auto mb-6">{t("prize_intro")}</p>
          <ul className="space-y-3 text-left max-w-md mx-auto font-['Outfit']">
            <li className="flex items-start gap-3 text-white/85"><span className="text-[#C5A059] mt-0.5">◆</span><span>{t("prize_1")}</span></li>
            <li className="flex items-start gap-3 text-white/85"><span className="text-[#C5A059] mt-0.5">◆</span><span>{t("prize_2")}</span></li>
            <li className="flex items-start gap-3 text-white/85"><span className="text-[#C5A059] mt-0.5">◆</span><span>{t("prize_3")}</span></li>
          </ul>
        </div>

        <form onSubmit={submit} className="space-y-12" data-testid="contest-form">
          {/* Slogan */}
          <section>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-white font-light mb-2">{t("slogan_title")}</h2>
            <p className="text-white/65 font-['Outfit'] text-sm mb-4">{lang === "en" ? config.slogan_prompt_en : config.slogan_prompt_sv}</p>
            <textarea value={slogan} onChange={(e) => setSlogan(e.target.value)} rows={5}
              className={`${inputCls} resize-none font-['Outfit']`}
              placeholder={lang === "en" ? "Write your slogan here..." : "Skriv din slogan här..."}
              data-testid="contest-slogan" />
            <div className="flex justify-between text-[11px] font-['Outfit'] mt-2 tracking-wider">
              <span className="text-white/35">{t("slogan_help")}</span>
              <span className={sloganOverLimit ? "text-red-400" : "text-white/50"}>
                {sloganWords}/150 {t("slogan_words")}{sloganOverLimit && ` — ${t("slogan_overlimit")}`}
              </span>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-white font-light mb-6">{t("contact_title")}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelCls}>{t("name_label")} *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} required data-testid="contest-name" />
              </div>
              <div>
                <label className={labelCls}>{t("email_label")} *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required data-testid="contest-email" />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t("phone_label")}</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="contest-phone" />
            </div>
          </section>

          {/* Terms */}
          <section>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-[#C5A059] cursor-pointer" required data-testid="contest-terms" />
              <span className="text-white/70 font-['Outfit'] text-sm">
                {t("terms_accept")}{" "}
                <button type="button" onClick={() => setShowTerms((s) => !s)} className="text-[#C5A059] underline hover:no-underline" data-testid="contest-show-terms">{t("terms_link")}</button>
              </span>
            </label>
            {showTerms && (
              <div className="mt-4 border border-[#262626] bg-[#0E0E0E] p-6 text-white/65 font-['Outfit'] text-[13px] leading-relaxed space-y-3" data-testid="contest-terms-text">
                <p><strong className="text-white/90">Anordnare:</strong> Pangea Nexus AB (org. 556822-5139), Nybohovsbacken 36 b, 117 63 Stockholm. Tävlingen genomförs i samarbete med Fotbollsmorgon och Scand Sports.</p>
                <p><strong className="text-white/90">Tävlingsperiod:</strong> Under hela VM 2026 (juni–juli 2026). En vinnare per kalendervecka utses.</p>
                <p><strong className="text-white/90">Bedömning:</strong> En jury utsedd av anordnaren bedömer samtliga inskickade sloganer utifrån originalitet, kreativitet och hur väl de fångar Serava Sports varumärke. Juryns beslut kan inte överklagas.</p>
                <p><strong className="text-white/90">Pris per vecka:</strong> 2 VIP-platser till Manchester United mot Atletico Madrid i Strawberry Arena 1 augusti 2026, 500 kr presentkort hos Serava Sports samt 2 biljetter till en Allsvensk match. Vinnaren meddelas via angiven e-post inom 7 dagar efter veckans slut.</p>
                <p><strong className="text-white/90">Personuppgifter:</strong> Uppgifter hanteras enligt GDPR enbart för att administrera tävlingen. Frågor: contact@seravagroup.com.</p>
              </div>
            )}
          </section>

          <button type="submit" disabled={submitting || !formValid}
            className="w-full sm:w-auto bg-[#C5A059] hover:bg-[#d1ad6b] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-['Outfit'] text-xs tracking-[0.18em] uppercase font-semibold px-10 py-4 transition-colors"
            data-testid="contest-submit">
            {submitting ? t("submitting") : t("submit")} →
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
